import React, { useRef, useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LISTAR_URL = "https://donnarosepratododia.com.br/listar_imagens.php";
const UPLOAD_URL = "https://donnarosepratododia.com.br/upload.php";
const EXCLUIR_URL = "https://donnarosepratododia.com.br/excluir_imagens.php";

function UploadPage() {
  // Arquivos locais (pré-visualização depois de escolher no input)
  const [arquivosLocais, setArquivosLocais] = useState([]);
  // Galeria real no servidor (URLs absolutas vindas do listar_imagens.php)
  const [galeria, setGaleria] = useState([]);
  // Itens selecionados (por URL)
  const [selecionadas, setSelecionadas] = useState(new Set());
  const [carregando, setCarregando] = useState(false);

  const janelaTVRef = useRef(null);

  // --- Util: atualiza galeria do servidor ---
  const refreshGaleria = async () => {
    try {
      const res = await fetch(`${LISTAR_URL}?t=${Date.now()}`, {
        cache: "no-store",
      });
      const data = await res.json();
      const imgs = Array.isArray(data)
        ? data
        : Array.isArray(data?.images)
        ? data.images
        : [];
      setGaleria(imgs);
      // mantém seleção apenas do que ainda existe
      setSelecionadas((prev) => {
        const keep = new Set();
        for (const u of prev) if (imgs.includes(u)) keep.add(u);
        return keep;
      });
    } catch (e) {
      console.error("Falha ao carregar galeria:", e);
      toast.error("Não foi possível carregar a galeria.");
    }
  };

  useEffect(() => {
    refreshGaleria();
  }, []);

  // --- Upload ---
  const handleFileChange = async (e) => {
    const novosArquivos = Array.from(e.target.files || []);
    if (!novosArquivos.length) return;

    // gera preview local (apenas visual)
    const comPreview = novosArquivos.map((f) => {
      f.previewUrl = URL.createObjectURL(f);
      return f;
    });
    setArquivosLocais((prev) => [...prev, ...comPreview]);

    setCarregando(true);
    let sucesso = 0;
    const erros = [];

    for (const imagem of novosArquivos) {
      const formData = new FormData();
      formData.append("imagem", imagem);

      try {
        const resp = await fetch(UPLOAD_URL, {
          method: "POST",
          body: formData,
        });
        const texto = (await resp.text()).toLowerCase();
        if (resp.ok && texto.includes("sucesso")) {
          sucesso++;
        } else {
          console.error("Upload falhou:", resp.status, texto);
          erros.push(imagem.name);
        }
      } catch (err) {
        console.error("Erro de rede no upload:", err);
        erros.push(imagem.name);
      }
    }

    setCarregando(false);

    if (sucesso > 0)
      toast.success(`✅ ${sucesso} imagem(ns) enviada(s) com sucesso!`);
    if (erros.length > 0) toast.error(`❌ Erro ao enviar: ${erros.join(", ")}`);

    // Atualiza lista real do servidor
    await refreshGaleria();

    // avisa a TV para atualizar
    try {
      const tv = janelaTVRef.current;
      if (tv && !tv.closed) {
        tv.postMessage({ type: "REFRESH_TV" }, window.location.origin);
        tv.focus();
      }
    } catch {}
  };

  // --- Remover pré-visualização local (não deleta do servidor) ---
  const removerPreviewLocal = (index) => {
    setArquivosLocais((prev) => {
      const arr = [...prev];
      const file = arr[index];
      if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
      arr.splice(index, 1);
      return arr;
    });
  };

  useEffect(() => {
    return () => {
      arquivosLocais.forEach(
        (f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl)
      );
    };
  }, [arquivosLocais]);

  // --- Seleção ---
  const alternarSelecao = (url) => {
    setSelecionadas((prev) => {
      const s = new Set(prev);
      if (s.has(url)) s.delete(url);
      else s.add(url);
      return s;
    });
  };

  const selecionarTudo = () => setSelecionadas(new Set(galeria));
  const limparSelecao = () => setSelecionadas(new Set());

  // --- Exclusão no servidor ---
  const excluirPorUrls = async (urls) => {
    if (!urls.length) return;
    try {
      const form = new FormData();
      urls.forEach((u) => form.append("urls[]", u)); // evita preflight/CORS

      const resp = await fetch(EXCLUIR_URL, { method: "POST", body: form });
      const data = await resp.json().catch(() => ({}));

      if (!resp.ok || data?.ok !== true) {
        throw new Error(data?.error || "Falha ao excluir.");
      }

      toast.success(`🗑️ ${urls.length} imagem(ns) excluída(s).`);

      // 1) Atualiza UI imediatamente
      setGaleria((prev) => prev.filter((u) => !urls.includes(u)));
      setSelecionadas((prev) => {
        const s = new Set(prev);
        urls.forEach((u) => s.delete(u));
        return s;
      });

      // 2) Avisa a TV recarregar
      try {
        const tv = janelaTVRef.current;
        if (tv && !tv.closed) {
          tv.postMessage({ type: "REFRESH_TV" }, window.location.origin);
          tv.focus();
        }
      } catch {}

      // 3) Refaz a leitura do servidor (garante que nada ficou em cache)
      await refreshGaleria();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Erro ao excluir.");
    }
  };

  const excluirSelecionadas = () => {
    const urls = Array.from(selecionadas);
    if (!urls.length) return;
    excluirPorUrls(urls);
  };

  const excluirUma = (url) => excluirPorUrls([url]);

  // --- Abrir janela da TV (/hoje) ---
  const abrirSlideshow = () => {
    if (!janelaTVRef.current || janelaTVRef.current.closed) {
      janelaTVRef.current = window.open("/hoje", "tvWindow");
    } else {
      janelaTVRef.current.focus();
    }
  };

  const tudoSelecionado =
    galeria.length > 0 && selecionadas.size === galeria.length;

  return (
    <>
      <div
        style={{
          textAlign: "center",
          marginTop: "30px",
          backgroundColor: "#000",
          color: "#fff",
          minHeight: "100vh",
          paddingBottom: "50px",
        }}
      >
        <h2>Prato do Dia | Donna Rose Marmitaria</h2>

        {/* UPLOAD */}
        <input
          id="upload-input"
          type="file"
          multiple
          accept="image/png, image/jpeg, image/webp"
          onChange={handleFileChange}
          style={{ display: "none" }}
          disabled={carregando}
        />

        <label
          htmlFor="upload-input"
          style={{
            backgroundColor: carregando ? "#777" : "#fff",
            color: "#000",
            padding: "10px 20px",
            borderRadius: "5px",
            cursor: carregando ? "not-allowed" : "pointer",
            fontWeight: "bold",
            display: "inline-block",
            marginRight: 10,
          }}
        >
          {carregando ? "Enviando..." : "Anexar Imagens"}
        </label>

        <button
          onClick={abrirSlideshow}
          style={{ fontSize: "16px", padding: "10px 20px" }}
        >
          Ver na TV
        </button>

        {/* AÇÕES DA GALERIA */}
        <div style={{ marginTop: 20 }}>
          <button
            onClick={tudoSelecionado ? limparSelecao : selecionarTudo}
            style={{
              background: "#fff",
              color: "#000",
              border: "none",
              padding: "8px 12px",
              borderRadius: "6px",
              marginRight: 10,
              cursor: "pointer",
            }}
          >
            {tudoSelecionado ? "Limpar seleção" : "Selecionar tudo"}
          </button>

          <button
            onClick={excluirSelecionadas}
            disabled={selecionadas.size === 0}
            title="Excluir selecionadas"
            style={{
              background: selecionadas.size === 0 ? "#444" : "red",
              color: "#fff",
              border: "none",
              padding: "8px 12px",
              borderRadius: "6px",
              cursor: selecionadas.size === 0 ? "not-allowed" : "pointer",
            }}
          >
            Excluir selecionadas
          </button>
        </div>

        {/* GALERIA DO SERVIDOR */}
        <h3 style={{ marginTop: 25 }}>Imagens no servidor</h3>
        {galeria.length === 0 ? (
          <p style={{ opacity: 0.8 }}>Nenhuma imagem enviada ainda.</p>
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "20px",
              marginTop: "10px",
            }}
          >
            {galeria.map((url, index) => {
              const marcado = selecionadas.has(url);
              return (
                <div
                  key={url}
                  style={{
                    position: "relative",
                    textAlign: "center",
                    width: 170,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 5,
                      left: 5,
                      backgroundColor: "#fff",
                      color: "#000",
                      padding: "2px 6px",
                      borderRadius: "50%",
                      fontWeight: "bold",
                    }}
                  >
                    {index + 1}
                  </div>

                  <img
                    src={url}
                    alt={`img-${index}`}
                    style={{
                      width: "150px",
                      height: "150px",
                      objectFit: "cover",
                      borderRadius: "10px",
                      border: marcado ? "3px solid #0f0" : "none",
                    }}
                  />

                  <div style={{ marginTop: 6 }}>
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={marcado}
                        onChange={() => alternarSelecao(url)}
                      />
                      Selecionar
                    </label>
                  </div>

                  <button
                    onClick={() => excluirUma(url)}
                    title="Excluir"
                    style={{
                      marginTop: "5px",
                      background: "red",
                      color: "#fff",
                      border: "none",
                      padding: "5px 10px",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  >
                    Excluir
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* PRÉ-VISUALIZAÇÃO LOCAL (opcional) */}
        {arquivosLocais.length > 0 && (
          <>
            <h3 style={{ marginTop: 30 }}>
              Pré-visualização local (ainda na memória)
            </h3>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "20px",
                marginTop: "10px",
              }}
            >
              {arquivosLocais.map((imagem, index) => (
                <div
                  key={index}
                  style={{ position: "relative", textAlign: "center" }}
                >
                  <img
                    src={imagem.previewUrl}
                    alt={`preview-${index}`}
                    style={{
                      width: "150px",
                      height: "150px",
                      objectFit: "cover",
                      borderRadius: "10px",
                    }}
                  />
                  <br />
                  <button
                    onClick={() => removerPreviewLocal(index)}
                    style={{
                      marginTop: "5px",
                      background: "#666",
                      color: "#fff",
                      border: "none",
                      padding: "5px 10px",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  >
                    Remover (local)
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
    </>
  );
}

export default UploadPage;
